import { gql } from '@apollo/client'

export const GET_TAX_REPORT = gql`
  query TaxReport($start: String!, $end: String!) {
    taxReport(start_date: $start, end_date: $end) {
      start_date
      end_date
      total_net_cents
      total_tax_cents
      total_gross_cents
      # Statiegeld — OUTSIDE the VAT base, so it is in none of the three totals
      # above and in no by_tax_rate bucket. Needed to reconcile the till:
      # total_gross_cents + total_deposit_cents == what was charged.
      total_deposit_cents
      by_tax_rate {
        tax_rate_percentage
        net_amount_cents
        tax_amount_cents
        gross_amount_cents
        order_line_count
      }
      by_payment_method {
        payment_method
        net_amount_cents
        tax_amount_cents
        gross_amount_cents
        order_line_count
      }
      daily_breakdown {
        date
        net_amount_cents
        tax_amount_cents
        gross_amount_cents
        deposit_total_cents
      }
    }
  }
`

export const GET_STAFF_CONSUMPTION_REPORT = gql`
  query StaffConsumptionReport($start: String!, $end: String!) {
    staffConsumptionReport(start_date: $start, end_date: $end) {
      period_start
      period_end
      total_value_cents
      total_orders
      rows {
        employee {
          id
          name
        }
        order_count
        total_value_cents
        items {
          product_name
          variant_name
          quantity
          value_cents
        }
      }
    }
  }
`

// Per-service-day slice of orderStats for the Daily Reports calendar. A
// SEPARATE document on purpose: adding byDay to GET_ORDER_STATS would 400 the
// Dashboard and Financial Overview until the API deploys the field. Sparse
// (zero-order days omitted); date = DATE(scheduled_for), the repo's day
// convention; money fields null without order.financial.view.
export const GET_DAILY_STATS = gql`
  query DailyStats($filters: OrderStatsFilters) {
    orderStats(filters: $filters) {
      byDay {
        date
        orderCount
        grossRevenueCents
        discountCents
        packagingFeeCents
        deliveryFeeCents
        deliveryFeeExVatCents
        turnoverExVatCents
        productSalesExVatCents
        cogsCents
        contributionMarginCents
        contributionMarginPct
        linesWithoutCogsCount
      }
    }
  }
`

// ─── Product Reports ─────────────────────────────────────────────────────────
// Contracts: tasks/product-reports-v2-api-handover.md (byProduct /
// byDayHourProduct / byWeekdayProduct / productVoidStats — the phase-2 surface)
// and tasks/product-reports-api-handover.md + its reply (the DEPLOYED v1
// surface this page still uses: productDayStats, byHourProduct, staffMeals,
// the ex-BTW money basis).
// Every document below is SEPARATE from the dashboard's
// GET_PRODUCT_SALES_STATS on purpose: they select args/fields (staffMeals,
// byHourProduct, productDayStats, and the phase-2 byProduct/byDayHourProduct/
// byWeekdayProduct/productVoidStats) that 400 until the API ships them — blast
// radius stays inside the Product Reports page. The dashboard keeps its legacy
// call (which still INCLUDES staff meals until the API's staffMeals default
// flips to EXCLUDE).

// Slim catalog index for the reconciliation join: every zero-sales row, the
// "archived" badge and the "not in menu" badge come from this, joined
// client-side on productId + variant name. Deliberately NOT the catalog page's
// GET_PRODUCTS — that document must stay free to change.
// `active` is intentionally UNFILTERED so archived products/variants come back
// too. `kind: BASE` matches what PRODUCED output contains: bundles/deals are
// exploded into their components, so a deal row could never be sold and would
// only add noise to the zero-sales list.
export const GET_MENU_INDEX = gql`
  query MenuIndex {
    products(filters: { kind: BASE }) {
      id
      name
      kind
      active
      variants {
        id
        name
        active
      }
    }
  }
`

// Reconciliation tab — one round trip, four aliased calls (different args →
// separate cache entries):
//   sold   PRODUCED units on customer orders,
//   staff  the same units on staff meals (Made = sold + staff),
//   prior  the shifted comparison period on the Made basis (INCLUDE),
//   revenue "as sold" euros — productDayStats is ORDERED/carrier-rule, and
//           PRODUCED rows carry no revenue by design, so the money column can
//           only come from here (summed per product client-side).
// PHASE 1 selects byHourProduct and sums it over the hours: that is
// period-exact, but the only key it exposes is productId + variantName.
// PHASE 2: when the API ships `byProduct`, sold/staff/prior switch to it
// (variantId-keyed, revenueExVatCents included) and the hour summation goes.
export const GET_PRODUCT_RECONCILIATION = gql`
  query ProductReconciliation($current: ProductStatsFilters, $prior: ProductStatsFilters) {
    sold: productSalesStats(filters: $current, view: PRODUCED, staffMeals: EXCLUDE) {
      byHourProduct {
        hour
        productId
        productName
        variantName
        qtyScheduled
      }
    }
    staff: productSalesStats(filters: $current, view: PRODUCED, staffMeals: ONLY) {
      byHourProduct {
        hour
        productId
        productName
        variantName
        qtyScheduled
      }
    }
    prior: productSalesStats(filters: $prior, view: PRODUCED, staffMeals: INCLUDE) {
      byHourProduct {
        hour
        productId
        productName
        variantName
        qtyScheduled
      }
    }
    revenue: productDayStats(filters: $current) {
      rows {
        productId
        variantName
        qtySold
        revenueExVatCents
      }
    }
  }
`

// NOT YET DEPLOYED. Cancelled/refunded orders are excluded from every other
// stats surface (ANALYTICS_QUALIFYING_WHERE_SQL), so per-product voids are new
// API surface. Order-level only: a partial refund is an amount and cannot be
// attributed to a product.
// Flip VOIDS_API_READY to true once the API ships productVoidStats — until then
// the tab skips the query and renders the two columns as "—".
export const VOIDS_API_READY = false

export const GET_PRODUCT_VOID_STATS = gql`
  query ProductVoidStats($filters: ProductStatsFilters) {
    productVoidStats(filters: $filters) {
      rows {
        productId
        variantId
        productName
        variantName
        qtyCancelled
        qtyRefunded
      }
    }
  }
`

// NOT YET DEPLOYED — this document 400s until the API ships byProduct /
// byDayHourProduct / byWeekdayProduct; the Planning tab and the drill panel's
// weekday sections contain that error locally.
// Requested contract: rows are SPARSE (no zero rows), isoDay is 1=Mon…7=Sun,
// weekStart is the Monday of the ISO week as "YYYY-MM-DD", and every bucket is
// Amsterdam-local on scheduled_for — the repo's day convention.
// `patterns` runs over the last 8 full weeks, `trend` over the last 13; both
// PRODUCED + INCLUDE, because this view is kitchen load, not sales.
export const GET_PRODUCT_WEEKDAY_REPORT = gql`
  query ProductWeekdayReport($patterns: ProductStatsFilters, $trend: ProductStatsFilters) {
    patterns: productSalesStats(filters: $patterns, view: PRODUCED, staffMeals: INCLUDE) {
      byProduct {
        productId
        variantId
        productName
        variantName
        qtySold
      }
      byDayHourProduct {
        productId
        variantId
        isoDay
        hour
        qty
      }
    }
    trend: productSalesStats(filters: $trend, view: PRODUCED, staffMeals: INCLUDE) {
      byWeekdayProduct {
        productId
        variantId
        isoDay
        weekStart
        qty
      }
    }
  }
`

// Per-product drill, section 1: (date × product) rows with margin, ALL money EX-BTW (the
// MarginService basis — API reply 2026-08-30; an incl-BTW gross minus ex-BTW
// COGS would overstate margin ~9%). Money fields are null without
// order.financial.view — render quantities and hide money, never error.
// cogs/margin are ALSO null when no attributed line carries COGS data (recipe
// missing) while revenue stays populated — linesWithoutCogsCount tells the
// cases apart. Carrier rule: a deal's premium revenue/discount and component
// COGS land on the parent line's row, or on the MAIN DISH's row for POS-built
// deals (no parent line exists) — every euro is on a visible row.
// staffMeals defaults to EXCLUDE server-side; the drill panel's toggle sends
// INCLUDE to fold staff meals into the quantities (their lines are zero-revenue).
export const GET_PRODUCT_DAY_STATS = gql`
  query ProductDayStats($filters: ProductStatsFilters, $staffMeals: StaffMealMode) {
    productDayStats(filters: $filters, staffMeals: $staffMeals) {
      rows {
        date
        productId
        variantId
        productName
        variantName
        qtySold
        revenueExVatCents
        discountAllocatedExVatCents
        cogsCents
        marginCents
        marginPct
        linesWithoutCogsCount
      }
    }
  }
`

// Sparse (iso_day 1–7 = Mon–Sun, hour 0–23) grid + daypart rollups, bucketed on
// scheduled_for in Europe/Amsterdam server-side. Money fields are null without
// order.financial.view; avg_prep_seconds covers ASAP orders only.
export const GET_TIME_SLOT_STATS = gql`
  query TimeSlots($filters: OrderStatsFilters) {
    timeSlotStats(filters: $filters) {
      by_hour {
        iso_day
        hour
        order_count
        revenue_cents
        turnover_ex_vat_cents
        delivery_fee_ex_vat_cents
        avg_order_value_cents
        avg_revenue_per_occurrence_cents
        avg_prep_seconds
        prep_sample_count
      }
      by_daypart {
        iso_day
        daypart
        daypart_label
        order_count
        revenue_cents
        turnover_ex_vat_cents
        delivery_fee_ex_vat_cents
        avg_order_value_cents
        avg_revenue_per_occurrence_cents
        avg_prep_seconds
        prep_sample_count
      }
      day_occurrences {
        iso_day
        count
      }
      dayparts {
        key
        label
        start_hour
        end_hour
      }
    }
  }
`

// Slot-filtered product mix for the heatmap drill-down. Separate from the
// dashboard's GET_PRODUCT_SALES_STATS on purpose — adding $slot/$view there
// would change the dashboard's call. In PRODUCED view revenueCents is null on
// every topProducts row (render units, hide revenue).
export const GET_PRODUCT_MIX_SLOT = gql`
  query ProductMixSlot($filters: ProductStatsFilters, $slot: ProductSlotFilter, $view: ProductMixView) {
    productSalesStats(filters: $filters, slot: $slot, view: $view) {
      topProducts(limit: 10) {
        productName
        variantName
        qtySold
        revenueCents
      }
      topUpgrades(limit: 8) {
        variantName
        count
        revenueCents
      }
      customizationFrequency {
        groupName
        totalCount
        options {
          label
          count
          priceDeltaCents
        }
      }
    }
  }
`
